import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  Button,
  Heading,
  UnorderedList,
  ListItem,
  useDisclosure,
  Divider,
  useToast,
  ModalBody,
  ModalFooter,
  Text,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { NavLink } from "react-router-dom";
import { CREATE_CLASS } from "../../graphql/mutations";
import { ORG_GRADES_SUBJECTS, MY_CLASSES } from "../../graphql/queries";
import { ClassList } from "../ClassList";
import { ModalBox } from "../ModalBox";
import { FormInput } from "../FormInput";
import { FormSelect } from "../FormSelect";
import { generateString } from "../../utils/functions";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { AlertDialog } from "../AlertDialog";
import { AddOrgModal } from "../AddOrgModal";

export const AppDrawer = ({
  isOpen,
  onClose,
  placement,
  navListItems = [],
  user,
  userRole,
}) => {
  const {
    isOpen: modalIsOpen,
    onOpen: modalOnOpen,
    onClose: modalOnClose,
  } = useDisclosure();
  const {
    isOpen: addOrgModalIsOpen,
    onOpen: addOrgModalOnOpen,
    onClose: addOrgModalOnClose,
  } = useDisclosure();
  const {
    isOpen: alertIsOpen,
    onOpen: alertOnOpen,
    onClose: alertOnClose,
  } = useDisclosure();
  const toast = useToast();
  const [selection, setSelection] = useState({
    className: "",
    grade: "",
    subject: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [myNewClass, setMyNewClass] = useState(null);
  const history = useHistory();
  const isTeacher = userRole === "Teacher";

  const [createNewClass] = useMutation(CREATE_CLASS, {
    update: (cache, { data }) => {
      const cached_class = cache.readQuery({
        query: MY_CLASSES,
        variables: {
          where: {
            OR: [
              {
                primaryCreator: {
                  id: {
                    equals: user.id,
                  },
                },
              },
              {
                creator: {
                  some: {
                    id: {
                      equals: user.id,
                    },
                  },
                },
              },
              {
                subscribers: {
                  some: {
                    id: {
                      equals: user.id,
                    },
                  },
                },
              },
            ],
          },
        },
      });
      cache.writeQuery({
        query: MY_CLASSES,
        variables: {
          where: {
            OR: [
              {
                primaryCreator: {
                  id: {
                    equals: user.id,
                  },
                },
              },
              {
                creator: {
                  some: {
                    id: {
                      equals: user.id,
                    },
                  },
                },
              },
              {
                subscribers: {
                  some: {
                    id: {
                      equals: user.id,
                    },
                  },
                },
              },
            ],
          },
        },
        data: {
          organizationClasses: [
            ...cached_class.organizationClasses,
            data.createOrganizationClass,
          ],
        },
      });
      setIsLoading(false);
      modalOnClose();
      setSelection({
        className: "",
        grade: "",
        subject: "",
      });
      toast({
        position: "top",
        title: "Class created",
        status: "success",
        duration: 9000,
        isClosable: true,
      });
    },
    onError: () => {
      setIsLoading(false);
      modalOnClose();
      setSelection({
        className: "",
        grade: "",
        subject: "",
      });
      toast({
        position: "top",
        title: "Failed to create class. Please try again",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    },
  });

  const { loading, data } = useQuery(ORG_GRADES_SUBJECTS, {
    variables: {
      gradeWhere: {
        organizationId: {
          equals: null,
        },
      },
      subjectWhere: {
        organizationId: {
          equals: null,
        },
      },
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const { data: myClasses } = useQuery(MY_CLASSES, {
    variables: {
      where: {
        OR: [
          {
            primaryCreator: {
              id: {
                equals: user.id,
              },
            },
          },
          {
            creator: {
              some: {
                id: {
                  equals: user.id,
                },
              },
            },
          },
          {
            subscribers: {
              some: {
                id: {
                  equals: user.id,
                },
              },
            },
          },
        ],
      },
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  useEffect(() => {
    if (
      userRole === "Student" &&
      !!myClasses &&
      myClasses.organizationClasses &&
      myClasses.organizationClasses.length > 0 &&
      window.location.pathname === "/"
    ) {
      history.push(`/class/${myClasses.organizationClasses[0].id}`);
    }
  }, [history, myClasses, userRole]);

  const onChange = (e, type) => {
    if (type === "className") {
      setSelection({
        ...selection,
        className: e.target.value,
        grade: data.organizationGrades[0].id,
        subject: data.organizationSubjects[0].id,
      });
    } else if (type === "grade") {
      setSelection({
        ...selection,
        grade: e.target.value,
        subject:
          selection.subject === ""
            ? data.organizationSubjects[0].id
            : selection.subject,
      });
    } else if (type === "subject") {
      setSelection({
        ...selection,
        grade:
          selection.grade === ""
            ? data.organizationGrades[0].id
            : selection.grade,
        subject: e.target.value,
      });
    }
  };

  const createClass = (user) => {
    if (selection.className === "") {
      toast({
        position: "top",
        title: "Please enter class name",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    } else {
      setIsLoading(true);
      createNewClass({
        variables: {
          data: {
            name: selection.className,
            classCode: generateString(6),
            isActive: true,
            grade: {
              connect: {
                id: selection.grade,
              },
            },
            subject: {
              connect: {
                id: selection.subject,
              },
            },
            primaryCreator: {
              connect: {
                id: user.id,
              },
            },
            creator: {
              connect: [
                {
                  id: user.id,
                },
              ],
            },
            ...(isTeacher
              ? {
                  organizations: {
                    connect: [{ id: user?.organizations[0]?.id }],
                  },
                }
              : {}),
          },
        },
      });
    }
  };

  return (
    <Drawer onClose={onClose} isOpen={isOpen} placement={placement}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader boxShadow="0 1px 9px -3px rgb(0 0 0 / 20%)">
          <Heading as="h4" size="md" textTransform="capitalize">
            {`${user?.firstName} ${user?.lastName}`}
          </Heading>
          <Text
            fontSize="sm"
            color="#424770"
            fontFamily="Glober, sans-serif"
            fontWeight="600"
            pt="10px"
          >
            Role: {userRole}
          </Text>
        </DrawerHeader>
        <DrawerBody padding="24px 0">
          <nav>
            <UnorderedList
              styleType="none"
              className="nav-list"
              spacing={0}
              margin="0"
            >
              {React.Children.toArray(
                navListItems.map(({ name, link }, index) => (
                  <ListItem
                    key={index}
                    margin="4px 0 8px"
                    height="40px"
                    lineHeight="40px"
                    color="#666"
                    _hover={{
                      background: "brand.500",
                      color: "#fff",
                    }}
                    className="nav-item"
                  >
                    <NavLink to={link} onClick={onClose} exact>
                      {name}
                    </NavLink>
                  </ListItem>
                ))
              )}
              {userRole !== "Student" && (
                <Button
                  leftIcon={<AddIcon />}
                  onClick={() => {
                    if (
                      (isTeacher && user?.organizations?.length > 0) ||
                      !isTeacher
                    ) {
                      modalOnOpen();
                    } else {
                      alertOnOpen();
                    }
                  }}
                  variant="outline"
                  colorScheme="blue"
                  margin="0 20px"
                  textTransform={"capitalize"}
                >
                  create class
                </Button>
              )}
            </UnorderedList>
          </nav>
          {userRole !== "Student" && <Divider margin="20px 0 0" />}
          <ClassList
            title="classes"
            onClose={onClose}
            userRole={userRole}
            classes={
              !!myClasses && myNewClass
                ? [...myClasses.organizationClasses, myNewClass]
                : !!myClasses && !myNewClass
                ? myClasses.organizationClasses
                : []
            }
          />
        </DrawerBody>
      </DrawerContent>
      <ModalBox
        isOpen={modalIsOpen}
        onClose={modalOnClose}
        title="Create Class"
      >
        <ModalBody pb={6}>
          <FormInput
            label={"class name"}
            inputOnChange={(e) => onChange(e, "className")}
            value={selection.className}
            formProps={{ isRequired: true }}
          />
          <FormSelect
            label={"grade"}
            selectOnChange={(e) => onChange(e, "grade")}
            value={selection.grade}
            isWorking={loading}
            options={
              !!data
                ? data.organizationGrades.map((grade) => ({
                    ...grade,
                    value: grade.id,
                    name: grade.gradeName,
                  }))
                : []
            }
          />
          <FormSelect
            label={"subject"}
            selectOnChange={(e) => onChange(e, "subject")}
            value={selection.subject}
            isWorking={loading}
            options={
              !!data
                ? data.organizationSubjects.map((subject) => ({
                    ...subject,
                    value: subject.id,
                    name: subject.subjectName,
                  }))
                : []
            }
          />
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="blue"
            mr={3}
            onClick={() => createClass(user)}
            isLoading={isLoading}
          >
            Create
          </Button>
          {!isLoading && <Button onClick={modalOnClose}>Cancel</Button>}
        </ModalFooter>
      </ModalBox>
      <AlertDialog
        title="Info"
        description="You haven't added any organization yet. Please add to create classes and adding students."
        actionButtonText="Add school or organization"
        isDeleteButton={false}
        isOpen={alertIsOpen}
        onCancel={alertOnClose}
        onDelete={() => {
          alertOnClose();
          addOrgModalOnOpen();
        }}
        isWorking={false}
        loadingText=""
      />
      <AddOrgModal
        user={user}
        isTeacher={userRole === "Teacher"}
        isOpen={addOrgModalIsOpen}
        onClose={addOrgModalOnClose}
      />
    </Drawer>
  );
};
